/**
 * addHighlight:
 *  対象ノードの親ノードをさかのぼっていきハイライト属性を付与
 *
 * @param {array} nodes node一覧
 * @param {object} selector 対象nodeId + ハイライト属性(nodeかedgeか)
 * @return {array} higlightを付与したnodeを返却
 */
export function addHighlight (nodes, selector) {
  let parent = selector.id
  let parentIndex = 0
  while (parent) {
    parentIndex = nodes.findIndex(({questionId}) => questionId === parent)
    nodes[parentIndex].highlight = true
    parent = nodes[parentIndex].parentId
  }
  return nodes
}
/**
 * addChilds:
 *  各nodeのparentIdから子nodeを計算し、childs配列に格納
 *
 * @param {array} nodes node一覧
 * @return {array} childsを付与したnodeを返却
 */
export function addChilds (nodes) {
  for (let i = 0; i < Object.keys(nodes).length; i++) {
    for (let j = 0; j < Object.keys(nodes).length; j++) {
      if (nodes[j].parentId) {
        if (nodes[i].questionId === nodes[j].parentId) {
          nodes[i].childs.push(nodes[j].questionId)
        }
      }
    }
  }
  return nodes
}
/**
 * addDummyChilds:
 *  位置座標の見栄えをよくするために、各ブランチの最も下の子なしnode(leaf node)にdummy nodeを付与する
 *  dummy nodeありで座標配置の計算することで横branch nodeとの衝突回避が起きて見栄えがよくなる
 *
 * @param {array} nodes node一覧
 * @return {array} dummy childsを付与したnodeを返却
 */
export function addDummyChilds (nodes) {
  let tempNodes = nodes
  for (const question of nodes) {
    if (question.childs.length === 0 && !question.isDummyChild && question.questionId !== 'temporaryId') {
      const dummy = {
        questionId: question.questionId + '_' + 'dummy',
        parentId: question.questionId,
        childs: [],
        layerNum: 0,
        posx: 0,
        posy: 0,
        highlight: false,
        isDummyChild: true,
        order: nodes[0].order
      }
      // dummy childsをquestions配列に入れる
      tempNodes.push(dummy)
      // dummy childsを追加した親nodeのchildsにdummy idを記載
      const parentIndex = tempNodes.findIndex(({questionId}) => questionId === question.questionId)
      tempNodes[parentIndex].childs.push(question.questionId + '_' + 'dummy')
    }
  }
  return tempNodes
}
/**
 * addLayerNum:
 *  各nodeが自分がroot nodeから見て何階層目のnodeであるかを親nodeから計算する
 *
 * @param {array} nodes node一覧
 * @return {array} layerNumを付与したnodeを返却
 */
export function addLayerNum (nodes) {
  for (let i = 0; i < Object.keys(nodes).length; i++) {
    let parentIndex = 0
    let parent = nodes[i].parentId
    let layerNum = 0
    while (parent) {
      layerNum++
      parentIndex = nodes.findIndex(({questionId}) => questionId === parent)
      parent = nodes[parentIndex].parentId
    }
    nodes[i].layerNum = layerNum
  }
  return nodes
}
/**
 * getMaxLayerNum:
 *  nodeの最大の階層数を計算して返却
 *
 * @param {array} nodes node一覧
 * @return {number} 最大の階層数
 */
export function getMaxLayerNum (nodes) {
  let count = 0
  for (const question of nodes) {
    if (question.layerNum > count) {
      count = question.layerNum
    }
  }
  return count
}
/**
 * SortByLayer:
 *  LayerでASC（昇順)にnodeをソートする
 *
 * @param {array} nodes node一覧
 * @param {number} maxLayerNum 最大階層
 * @return {array} 階層でソートされたnodes
 */
export function SortByLayer (nodes, maxLayerNum) {
  const buffer = []
  for (let i = 0; i <= maxLayerNum; i++) {
    for (let j = 0; j < Object.keys(nodes).length; j++) {
      if (nodes[j].layerNum === i) {
        buffer.push(nodes[j])
      }
    }
  }
  return buffer
}
/**
 * sortChildsArraybyOrder
 *  nodeのorder順で配列をソートする
 *
 * @param {array} childIdの配列
 * @param {array} nodesの配列
 * @return {array} ソートされたnodesのchildId配列
 */
function sortChildsArraybyOrder (childIdArray, nodes) {
  let array = []
  for (const childId of childIdArray) {
    const childIndex = nodes.findIndex(({questionId}) => questionId === childId)
    array.push(nodes[childIndex])
  }
  array.sort(function (a, b) {
    if (a.order > b.order) {
      return 1
    } else {
      return -1
    }
  })
  let childs = []
  for (const node of array) {
    childs.push(node.questionId)
  }
  return childs
}
/**
 * SortByChilds:
 *  ChildsでASC（昇順)にnodeをソートする
 *  Layer sort後なのでrootノードから順に各階層の子nodeを計算して配置していくことになる
 *
 * @param {array} nodes node一覧
 * @return {array} childsでソートされたnodes
 */
export function SortByChilds (nodes) {
  let buffer = []
  let childIndex = 0
  // root node
  buffer.push(nodes[0])
  for (let i = 0; i < Object.keys(nodes).length; i++) {
    const sortedChilds = sortChildsArraybyOrder(buffer[i].childs, nodes)
    for (const child of sortedChilds) {
      childIndex = nodes.findIndex(({questionId}) => questionId === child)
      buffer.push(nodes[childIndex])
    }
  }
  return buffer
}
/**
 * setNodesAtEqualIntervals:
 *  nodeを等間隔で配置する
 *
 * @param {array} nodes node一覧
 * @param {array} layerArrays 各layerに入っているnodes配列
 * @param {number} maxLayerNum 最大階層
 * @param {number} nodeWidth nodeの幅
 * @return {array} 等間隔配置が付与されたnodes
 */
export function setNodesAtEqualIntervals (nodes, layerArrays, maxLayerNum, nodeWidth) {
  let layerIndex = 0
  let layerArrayNum = 0
  for (let i = maxLayerNum; i >= 0; i--) {
    layerArrayNum = 0
    layerIndex = 0
    for (const layerArray of layerArrays[i]) {
      layerIndex = nodes.findIndex(({questionId}) => questionId === layerArray.questionId)
      nodes[layerIndex].posx += nodeWidth * layerArrayNum
      layerArrayNum++
    }
  }
  return nodes
}
/**
 * setNodesPositionFromChilds:
 *  nodeをchilds順で等間隔に配置してchilds位置が相互干渉しないようにする
 *
 * @param {array} nodes node一覧
 * @param {array} layerArrays 各layerに入っているnodes配列
 * @param {number} maxLayerNum 最大階層
 * @return {array} 子供で等間隔配置が付与されたnodes
 */
export function setNodesPositionFromChilds (nodes, layerArrays, maxLayerNum) {
  for (let i = maxLayerNum - 1; i >= 0; i--) {
    let layerIndex = 0
    let childIndex = 0
    // 大きいLayer順でループを回す
    for (const layerArray of layerArrays[i]) {
      layerIndex = nodes.findIndex(({questionId}) => questionId === layerArray.questionId)
      // layerIndexのnodeが子持ちの場合、子供の中心座標を求める
      if (layerArray.childs.length > 0) {
        // console.log('child持ち ' + 'id: ' + layerIndex)
        let posx = 0
        let delta = 0
        for (const child of layerArray.childs) {
          childIndex = nodes.findIndex(({questionId}) => questionId === child)
          posx += nodes[childIndex].posx
        }
        posx = posx / layerArray.childs.length
        delta = posx - nodes[layerIndex].posx
        // 子の中心座標と自身の座標を比較し、子のほうが大きいなら親座標をSHIFT
        if (posx >= nodes[layerIndex].posx) {
          // 親Layerのループを回す
          for (const layerArray of layerArrays[i]) {
            let countIndex = nodes.findIndex(({questionId}) => questionId === layerArray.questionId)
            // console.log('countIndex: ' + countIndex + ' layerIndex: ' + layerIndex)
            if (countIndex >= layerIndex) {
              nodes[countIndex].posx += delta
              // console.log('parent移動 ' + 'id: ' + countIndex + '  posx: ' + nodes[countIndex].posx)
            }
          }
          // 子の中心座標と自身の座標を比較し、親のほうが大きいなら子をSHIFT
        } else {
          // 子Layerのループを回す
          // console.log('id: ' + layerIndex + '  posx: ' + nodes[layerIndex].posx)
          let childNum = nodes.findIndex(({questionId}) => questionId === layerArray.childs[0])
          // console.log('childNum: ' + childNum)
          for (const layerArray of layerArrays[i + 1]) {
            childIndex = nodes.findIndex(({questionId}) => questionId === layerArray.questionId)
            if (childIndex >= childNum) {
              nodes[childIndex].posx += -delta
              // console.log('child移動 ' + 'id: ' + childIndex + '  posx: ' + nodes[childIndex].posx)
            }
          }
        }
      }
      // console.log('決まった座標 ' + 'id: ' + layerIndex + '  posx: ' + nodes[layerIndex].posx)
    }
  }
  return nodes
}
/**
 * setNodesPositionFromParent:
 *  nodeを親順で等間隔に配置して親位置が相互干渉しないようにする
 *
 * @param {array} nodes node一覧
 * @return {array} 親で等間隔配置が付与されたnodes
 */
export function setNodesPositionFromParent (nodes, layerArrays, maxLayerNum) {
  for (let i = 0; i < maxLayerNum; i++) {
    let layerIndex = 0
    let childIndex = 0
    for (const layerArray of layerArrays[i]) {
      layerIndex = nodes.findIndex(({questionId}) => questionId === layerArray.questionId)

      if (layerArray.childs.length > 0) {
        let posx = 0
        let delta = 0
        for (const child of layerArray.childs) {
          childIndex = nodes.findIndex(({questionId}) => questionId === child)
          posx += nodes[childIndex].posx
        }
        posx = posx / layerArray.childs.length
        delta = posx - nodes[layerIndex].posx
        // 子の中心座標と自身の座標を比較し、親のほうが大きいなら子をSHIFT
        if (posx < nodes[layerIndex].posx) {
          // 子Layerのループを回す
          for (const child of layerArray.childs) {
            childIndex = nodes.findIndex(({questionId}) => questionId === child)
            nodes[childIndex].posx += -delta
          }
        }
      }
      // console.log('決まった座標 ' + 'id: ' + layerIndex + '  posx: ' + nodes[layerIndex].posx)
    }
  }
  return nodes
}
/**
 * deleteDummyChilds:
 *  配置計算用に追加したdummy nodeを消去する
 *
 * @param {array} nodes node一覧
 * @return {array} dymmy nodesが削除されたnodes
 */
export function deleteDummyChilds (nodes) {
  let tempNodes = []
  for (const question of nodes) {
    if (!question.isDummyChild) {
      tempNodes.push(question)
    }
  }
  return tempNodes
}
/**
 * getLayerNumber:
 *  自身の階層を返却する
 *
 * @param {string} id nodeのquestionId
 * @param {array} questions node一覧
 * @return {number} 自身の階層
 */
export function getLayerNumber (id, questions) {
  let layerNumber = 0
  for (;;) {
    let index = questions.findIndex(({questionId}) => questionId === id)
    id = questions[index].parentId
    if (!id) { break }
    layerNumber += 1
  }
  return layerNumber
}
/**
 * matchAnchorNumber:
 *  nodesの中に対象のanchor numが存在するかを確認
 *
 * @param {number} num anchor数値
 * @param {array} questions node一覧
 * @return {boolean} numが存在すればtrue
 */
function matchAnchorNumber (num, questions) {
  let flag = false
  for (const question of questions) {
    if (question.number === num) {
      flag = true
    }
  }
  return flag
}
/**
 * getNumbesrforAnchor:
 *  対象のnodeのtextの中からanchor numが存在すれば配列で返す
 *
 * @param {string} message 対象nodeのtext
 * @param {number} fromNum 自身のnumber
 * @param {array} questions nodes一覧
 * @return {array} 該当nodeのnumが入った配列
 */
export function getNumbesrforAnchor (message, fromNum, questions) {
  if (!message) { return }
  if (!message.match(/#\d+/)) { return }
  const matches = Array.from(new Set(message.match(/#\d+/g)))
  let array = []
  for (const match of matches) {
    const num = Number(match.slice(1))// 頭文字削除
    if (!matchAnchorNumber(num, questions)) { continue }
    // fromとtoのNumが同じならリターン
    if (num === fromNum) { continue }
    // rootもリターン
    if (num === 0) { continue }
    array.push(num)
  }
  return array
}
/**
 * addEdgePosition:
 *  各nodesの端の座標を算出して追加する
 *
 * @param {array} questions nodes一覧
 * @param {number} nodeHeight nodeの高さ
 * @param {number} nodeMarginY nodeの高さマージン
 * @return {array} 計算結果の入ったnodes
 */
export function addEdgePosition (questions, nodeHeight, nodeMarginY) {
  for (let question of questions) {
    const parent = questions.find(elm => elm.questionId === question.parentId)
    if (parent) {
      question.x1 = parent.posx
      question.y1 = parent.posy + (nodeHeight - nodeMarginY)
      question.x2 = question.posx
      question.y2 = question.posy
    } else {
      question.x1 = 0
      question.y1 = 0
      question.x2 = 0
      question.y2 = 0
    }
  }
  return questions
}
/**
 * getAnchorQuestionIdfromNumber:
 *  各nodesのnumberからquestionIdを算出する
 *
 * @param {number} fromNumber 対象nodeのnumber
 * @param {number} anchorNumbers アンカー先のnumber配列
 * @param {array} questions nodes一覧
 * @return {array} fromとtoのquestionId配列
 */
export function getAnchorQuestionIdfromNumber (fromNumber, anchorNumbers, graphDisplayData) {
  if (!anchorNumbers) { return }
  const fromIndex = graphDisplayData.findIndex(({number}) => number === fromNumber)
  if (fromIndex === -1) { return } // fromIndexが-1の場合はdummyNodeなのでanchorの計算しない.
  const fromGraph = graphDisplayData[fromIndex]
  let toGraphs = []
  for (const num of anchorNumbers) {
    const toIndex = graphDisplayData.findIndex(({number}) => number === num)
    toGraphs.push(graphDisplayData[toIndex])
  }
  return [fromGraph, toGraphs]
}
/**
 * getBrotherNodes :
 *  対象nodeとnodes一覧から対象nodeの兄弟nodeを算出して返却
 *
 * @param {object} node 対象のnode
 * @param {array} nodes nodes一覧
 * @return {array} 兄弟nodes一覧
 */
export function getBrotherNodes (node, nodes) {
  let brothers = []
  // root nodeは親も兄弟もいない
  if (!node.parentId) { return brothers }
  const parentIndex = nodes.findIndex(({questionId}) => questionId === node.parentId)
  // 子供が1人以下
  if (nodes[parentIndex].childs.length <= 1) { return brothers }
  // 子供が2人以上=兄弟がいる
  for (const childId of nodes[parentIndex].childs) {
    if (childId !== node.questionId) {
      const targetIndex = nodes.findIndex(({questionId}) => questionId === childId)
      brothers.push(nodes[targetIndex])
    }
  }
  return brothers
}
/**
 * sortArraybyOrder
 *  nodeのorder順で配列をソートする
 *
 * @param {array} nodesの配列
 * @return {array} ソートされたnodes配列
 */
function sortArraybyOrder (nodes) {
  let array = nodes
  array.sort(function (a, b) {
    if (a.order > b.order) {
      return 1
    } else {
      return -1
    }
  })
  return array
}
/**
 * getBrotherSegments :
 *  対象nodeと対象nodesのbrother nodes一覧から並び替えが発動する範囲を算出
 *
 * @param {object} node 対象のnode
 * @param {array} nodes brothernodesの一覧
 * @param {Number} nodeWidth nodeの幅
 * @param {Number} nodeMarginX node間のmargin幅
 * @return {array} 並び替えを判断するセグメントブロック配列
 */
export function getBrotherSegments (node, brothers, nodeWidth, nodeMarginX) {
  let segments = []
  const array = sortArraybyOrder(brothers)
  const targetX = node.posx

  for (let i = 0; i < array.length; ++i) {
    if (segments.length === 0) { // 一番左端のSegment
      // -10000秒加算
      const preTime = array[i].order - 10000
      let segment = {
        order: preTime,
        x1: array[i].posx - 3 * (nodeWidth - nodeMarginX),
        x2: array[i].posx - nodeMarginX,
        y: array[i].posy,
        index: segments.length,
        valid: true
      }
      // 対象nodeがそのsegment内に存在する場合、そのsegmentは無効
      if (segment.x1 < targetX && targetX < segment.x2) {
        segment.valid = false
      }
      segments.push(segment)
    }
    if (array[i + 1]) { // array[i]とarray[i+1]の間のSegment
      // 時刻の差分計算
      const diffTime = array[i].order + ((array[i + 1].order - array[i].order) / 2.0)
      const segment = {
        order: diffTime,
        x1: array[i].posx,
        x2: array[i + 1].posx - nodeMarginX,
        y: array[i].posy,
        index: segments.length,
        valid: true
      }
      // 対象nodeがそのsegment内に存在する場合、そのsegmentは無効
      if (segment.x1 < targetX && targetX < segment.x2) {
        segment.valid = false
      }
      segments.push(segment)
    } else { // 一番右端のSegment
      const segment = {
        order: Date.now(),
        x1: array[i].posx,
        x2: array[i].posx + 3 * (nodeWidth - nodeMarginX),
        y: array[i].posy,
        index: segments.length,
        valid: true
      }
      // 対象nodeがそのsegment内に存在する場合、そのsegmentは無効
      if (segment.x1 < targetX && targetX < segment.x2) {
        segment.valid = false
      }
      segments.push(segment)
    }
  }
  return segments
}

/**
 * getBoardLayerNum :
 *  対象cardのLayerを計算して返却
 *
 * @param {array} arrayTemp boardのcards一覧
 * @return {array} layerNumを追加したcards一覧
 */
export function getBoardLayerNum (arrayTmp) {
  let array = []
  let count = 0
  for (const card of arrayTmp) {
    let tempCard = card
    tempCard.layerNum = count
    count++
    array.push(tempCard)
  }
  return array
}
/**
 * getBoardCardPosition :
 *  対象cardのLayerを計算して返却
 *
 * @param {array} arrayTemp boardのcards一覧
 * @return {array} layerNumを追加したcards一覧
 */
export function getBoardCardPosition (arrayTmp, graphWidth, nodeWidth, nodeMarginX, nodeHeight, nodeMarginY, scale, nodes) {
  let array = []
  // root nodeが必ず描画のcenterのため、root nodeの座標を代入
  const centerPosX = nodes[0].posx - nodeMarginX / 2
  // 画面centerのrootPos + graphWidth/2が右端の座標
  const graphRightX = centerPosX + (graphWidth / 2) / scale
  for (const card of arrayTmp) {
    let tempCard = card
    tempCard.posx += graphRightX
    tempCard.posy = tempCard.layerNum * nodeHeight
    array.push(tempCard)
  }
  return array
}
/**
 * getBoardSize :
 *  対象cardからboardの開始点(x0, y0)と高さを返す
 *
 * @param {array} arrayTemp boardのcards一覧
 * @return {array} minX, minY, heightを返却
 */
export function getBoardSize (arrayTmp) {
  let minX = arrayTmp[0].posx
  let minY = arrayTmp[0].posy
  let maxY = arrayTmp[0].posy
  for (const card of arrayTmp) {
    minX = Math.min(minX, card.posx)
    minY = Math.min(minY, card.posy)
    maxY = Math.max(maxY, card.posy)
  }
  return [minX, minY, (maxY - minY)]
}



// WEBPACK FOOTER //
// ./src/pages/tree/graph/graphUtility.js